import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatMoney, formatMoneyCompact } from '@orgazone/money-format';
import { isLegacyRegister } from '@orgazone/core-nf525';
import { formatRateBp } from '@orgazone/core-vat';
import { EmptyState, Icon, Segmented, Tabs, Trans, useLatest, type IconName } from '@orgazone/ui-web';
import { horodatageFiscal, jourYmd, useHorlogeMinute } from '../periode';
import { currentLang } from '../lang';
import { api, type CashBlocker, type CashDayView, type CashMonth, type CashMonthDay, type CashMonthDayRegister, type CashMonthsView, type CashRegisterDay, type SessionCount, type SessionDeposit, type SessionDetail } from '../api';
import { useStoreScope } from '../store-context';
import { REGLEMENTS, libelleReglement } from '../reglements';
import { codeCaisse } from '../caisse';
import { pctBp } from '@orgazone/core-money';
import { key, t, type Translatable } from '../i18n';

/** ⚠️ Le squelette d'une journée vide n'a que le `register_id` : le drapeau du serveur n'existe pas
 *  pour une caisse qui n'a rien produit. La sentinelle de reprise, elle, est dans l'identifiant.
 *  ⚠️ LA RÈGLE VIENT DU CŒUR, elle n'est plus recopiée ici : la Console ÉCRIT ce préfixe, et il était
 *  relu par trois consommateurs qui en portaient chacun leur littéral. Un seul qui change de forme, et
 *  la reprise devient invisible à l'écran sans la moindre erreur. */
const estCaisseReprise = isLegacyRegister;

/**
 * Z & CLÔTURES — l'état des caisses d'une journée, et ce qui empêche de clôturer le mois.
 *
 * UTILITÉ : c'est l'écran qui répond à « est-ce que tout le monde a fermé, et est-ce que ça tombe
 * juste ? ». Tout vient des caisses : le back-office lit et conserve, il ne recalcule pas un Z.
 *
 * ⚠️ LE BACK-OFFICE NE CLÔTURE JAMAIS À LA PLACE D'UNE CAISSE : seule la caisse signe son Z. Une
 * caisse encore ouverte s'AFFICHE comme telle — c'est la vérité, pas un écran en panne.
 *
 * ⚠️ LA JOURNÉE EST UNE JOURNÉE FISCALE (§9.4bis), pas une date de calendrier : la caisse l'a décidée
 * à l'écriture, bascule comprise. L'écran l'envoie telle quelle et ne convertit rien.
 *
 * ⚠️ CE QUI N'EST PAS ICI, ET POURQUOI — chaque retrait vient d'un mécanisme absent, pas d'un choix
 * d'interface. La RELANCE d'une caisse : aucun canal ne descend un message vers une caisse. Le bouton
 * « CLÔTURER DÉFINITIVEMENT » et sa modale : une clôture mensuelle est une pièce SIGNÉE (`GTM`), et
 * seule une caisse la scelle — le back-office constate qu'elle existe ou qu'elle manque. « RÉIMPRIMER
 * LE Z » / « EXPORTER LE Z » : le duplicata est signé par la caisse, sur son imprimante. Le DÉPLIAGE
 * DES ÉCARTS PAR MODE (espèces / CB / chèques) : seul l'écart ESPÈCES existe (comptage du tiroir) —
 * le rapprochement TPE et la remise de chèques n'ont aucune source, et remplir ces colonnes serait
 * inventer. Enfin l'état « rien à clôturer sur ce site » (dépôt) attend que le type de site atteigne
 * le contexte magasin. Ce qui n'est pas branché s'enlève, il ne se simule pas (§23.0).
 */
export function Cloture() {
  const scope = useStoreScope();
  const gln = scope.view?.active?.gln ?? null;

  const [onglet, setOnglet] = useState<'jour' | 'mois'>('jour');
  // ⚠️ LA BASCULE ARRIVE AVEC LA JOURNÉE, pas avant : au tout premier rendu on ne l'a pas encore, et
  //    le jour calendaire est le repli le plus strict (bascule 0). Dès la réponse reçue, le jour
  //    courant devient FISCAL — sans quoi un commerce en nocturne se croirait demain à 00h30.
  const [bascule, setBascule] = useState(0);
  const now = useHorlogeMinute();
  const today = useMemo(() => jourFiscalCourant(now, bascule), [now, bascule]);
  const [day, setJour] = useState(() => jourYmd(new Date()));
  const [month, setMois] = useState<CashMonthsView | null>(null);
  const [zOuvert, setZOuvert] = useState<CashRegisterDay | null>(null);
  const [vue, setVue] = useState<CashDayView | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // ⚠️ SEULE LA DERNIÈRE LECTURE ÉCRIT L'ÉCRAN : on feuillette les jours plus vite que le serveur ne répond — la
  //    journée de la veille, revenue en dernier, s'afficherait sous la date du jour choisi.
  const latestDay = useLatest();
  const reload = useCallback(() => {
    if (!gln) return;
    const isLatest = latestDay();
    setLoading(true);
    setErreur(null);
    api.cashDay(day)
      .then((v) => { if (!isLatest()) return; setVue(v); setBascule(v.dayCutoffMinutes); setLoading(false); })
      // ⚠️ LE MESSAGE RÉEL, et l'état de succès NON appliqué : une journée qu'on n'a pas pu lire ne
      //    doit jamais ressembler à une journée sans caisse (§23.0).
      .catch((e: unknown) => { if (!isLatest()) return; setErreur(e instanceof Error ? e.message : String(e)); setLoading(false); });
  }, [gln, day, latestDay]);

  useEffect(reload, [reload]);

  // ⚠️ LE RELEVÉ MENSUEL SE CHARGE À PART, et son échec ne fait pas tomber la journée : ce sont deux
  //    lectures indépendantes, et perdre l'état des caisses parce qu'un agrégat mensuel a échoué
  //    priverait l'écran de sa raison d'être.
  useEffect(() => {
    if (!gln) return;
    let vivant = true;
    api.cashMonths().then((m) => { if (vivant) setMois(m); }).catch(() => { if (vivant) setMois(null); });
    return () => { vivant = false; };
  }, [gln]);

  const decaler = (n: number) => {
    const d = new Date(Number(day.slice(0, 4)), Number(day.slice(4, 6)) - 1, Number(day.slice(6, 8)) + n);
    const y = jourYmd(d);
    if (y <= today) setJour(y);
  };

  const variances = (vue?.registers ?? []).filter((r) => r.varianceCents !== null && r.varianceCents !== 0);

  // ⚠️ LE SQUELETTE D'UNE JOURNÉE SANS OUVERTURE, et il n'invente rien : chaque vignette ajoutée
  //    correspond à une caisse que ce magasin a RÉELLEMENT fait tourner, et affiche « Caisse non
  //    ouverte » — ce qui est vrai. Un jour de fermeture ressemblait sinon à un écran en panne.
  const registers = useMemo(() => completerCaisses(vue), [vue]);
  // ⚠️ AVANT LE PREMIER JOUR D'ACTIVITÉ, PAS DE SQUELETTE : ce n'est pas une journée de fermeture,
  //    c'est une journée où le magasin n'existait pas. Les confondre ferait passer l'un pour l'autre.
  const avantOuverture = vue?.firstActivityDay != null && day < vue.firstActivityDay;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>
            
            {t('Z & clôtures')}
          </h1>
          <p style={{ margin: '3px 0 0', fontSize: '13.5px', color: 'var(--ink-500)' }}>
            <Trans k='Le Z du jour <1>vient des caisses</1>. La clôture du mois <2>part d’ici</2>.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></p>
        </div>
      </div>

      <BarreOnglets actif={onglet} onChanger={setOnglet}
        aJustifier={variances.filter((e) => !e.justification).length}
        blocages={month?.blockers.length ?? 0} />

      {/* ⚠️ LE PANNEAU EXISTE, et porte l’identifiant que la barre annonce : un `aria-controls` qui
          pointe dans le vide déclare au lecteur d’écran un motif que la page ne rend pas. */}
      <div id="bo-cloture" role="tabpanel" aria-labelledby={`bo-cloture-tab-${onglet}`}>

      {onglet === 'mois' ? (
        <OngletMensuel vue={month} onVoirJournee={(d) => { setJour(d); setOnglet('jour'); }} />
      ) : (
      <>
      <NavigateurDeJour
        day={day} today={today}
        onDecaler={decaler} onChoisir={setJour} onAujourdhui={() => setJour(today)}
      />

      {!gln ? (
        <EmptyState icon="store" title={t('Aucun magasin actif')}
          note={t('Choisissez le magasin à administrer pour voir l’état de ses caisses.')} />
      ) : erreur ? (
        <EmptyState icon="alert" title={t('Journée illisible')} note={erreur} />
      ) : loading ? (
        <EmptyState icon="clock" title={t('Lecture de la journée…')} note={t('Les caisses de cette journée sont en cours de lecture.')} />
      ) : !vue || avantOuverture || registers.length === 0 ? (
        /* ⚠️ LE BANDEAU DES SESSIONS NON DATÉES DOIT SURVIVRE À L'ÉTAT VIDE, et c'est tout son intérêt :
           une session sans journée fiscale rend justement cette journée vide. Le réserver au cas où des
           caisses s'affichent le cachait exactement quand il fallait le lire — « aucune caisse » sans
           explication est le contresens qu'il existe pour empêcher. */
        <>
          <EmptyState icon="drawer"
            title={avantOuverture ? t('Journée antérieure à ce magasin') : t('Aucune caisse connue')}
            note={avantOuverture
              ? t('La première activité enregistrée ici date du {v}. Avant cette date, il n’y a rien à afficher.', { v: vue?.firstActivityDay ? jourEnToutesLettres(vue.firstActivityDay) : '—' })
              : t('Aucune caisse n’a jamais ouvert ni encaissé dans ce magasin.')} />
          {vue && vue.undatedSessions > 0 ? <SessionsNonDatees n={vue.undatedSessions} /> : null}
        </>
      ) : (
        <>
          {/* ⚠️ `auto-fill` ET NON `auto-fit` : `auto-fit` FAIT DISPARAÎTRE les colonnes vides, et une
              caisse seule s'étirait alors sur toute la largeur. La maquette en montre trois, le défaut
              y était donc invisible. `auto-fill` garde les colonnes, la vignette garde sa taille. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(276px,1fr))', gap: 13, marginBottom: 16 }}>
            {registers.map((r) => (
              <CarteCaisse key={`${r.registerId}-${r.sessionId ?? ''}`} r={r} onOuvrirZ={() => setZOuvert(r)} revolue={day < today} />
            ))}
          </div>

          {variances.length > 0 ? (
            <SectionEcarts variances={variances} motifs={vue.motifs} onEnregistre={reload} />
          ) : null}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 13 }}>
            <VentilationTva rows={vue.vat} total={vue.totalInclCents} titles={vue.titlesIssuedCents} />
            <ReglementsDuJour rows={vue.payments} total={vue.totalInclCents} />
          </div>

          {vue.undatedSessions > 0 ? <SessionsNonDatees n={vue.undatedSessions} /> : null}
        </>
      )}
      </>
      )}
      </div>
      {zOuvert ? <ModaleZ registre={zOuvert} onClose={() => setZOuvert(null)} /> : null}
    </div>
  );
}

/**
 * LES DEUX ONGLETS, avec ce qu'ils ont à traiter.
 *
 * ⚠️ LE COMPTEUR N'APPARAÎT QUE S'IL Y A QUELQUE CHOSE : une pastille « 0 » attire l'œil vers un
 * onglet où il n'y a rien à faire, et finit par ne plus rien signifier quand elle en porte un vrai.
 */
function BarreOnglets({ actif, onChanger, aJustifier, blocages }: {
  actif: 'jour' | 'mois'; onChanger: (o: 'jour' | 'mois') => void;
  aJustifier: number; blocages: number;
}) {
  // ⚠️ BARRE DU SOCLE, PAS UNE COPIE. Elle portait les mêmes onglets et les mêmes compteurs,
  // redessinés à la main : `Tabs` les rend déjà (`count` + `countTone`), avec la tabulation
  // glissante, les flèches, et le correctif d'ascenseur que toute barre refaite à la main reperd.
  return (
    <Tabs
      items={[
        { id: 'jour', label: t('Journée'), count: aJustifier, countTone: 'warn' },
        { id: 'mois', label: t('Clôtures mensuelles'), count: blocages, countTone: 'warn' },
      ]}
      value={actif}
      onChange={onChanger}
      panelId="bo-cloture"
      label={t('Vues de la clôture')}
      size="md"
    />
  );
}

/** Bordure, hauteur et rayon communs aux contrôles du navigateur de jour — la maquette les aligne tous. */
const CONTROLE: React.CSSProperties = {
  height: 40, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)',
  background: 'var(--paper)', flexShrink: 0,
};

/**
 * LE CHOIX DE LA JOURNÉE — flèches, calendrier, et ce que vaut la journée regardée.
 *
 * ⚠️ AUCUN JOUR APRÈS AUJOURD'HUI : les flèches et le calendrier sont bornés, parce qu'une journée à
 * venir n'a rien à afficher et qu'un écran vide y ressemblerait à une panne.
 */
function NavigateurDeJour({ day, today, onDecaler, onChoisir, onAujourdhui }: {
  day: string; today: string;
  onDecaler: (n: number) => void; onChoisir: (y: string) => void; onAujourdhui: () => void;
}) {
  const iso = `${day.slice(0, 4)}-${day.slice(4, 6)}-${day.slice(6, 8)}`;
  const maxIso = `${today.slice(0, 4)}-${today.slice(4, 6)}-${today.slice(6, 8)}`;
  const cEst = day === today;
  const ctx = cEst
    // ⚠️ « EN COURS » SE DIT DE LA JOURNÉE FISCALE, pas du jour calendaire : à 00h30 avec une bascule
    //    à 02:00, la soirée du commerçant est toujours en cours.
    ? { l: t('Journée fiscale en cours'), ic: 'clock' as IconName, bg: 'var(--amber-bg)', fg: 'var(--amber-fg)' }
    : { l: t('Journée révolue'), ic: 'check' as IconName, bg: 'var(--teal-100)', fg: 'var(--teal-800)' };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap', marginBottom: 15 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        <button type="button" onClick={() => onDecaler(-1)} aria-label={t('Jour précédent')}
          style={{ ...CONTROLE, width: 40, padding: 0, color: 'var(--ink-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <Icon name="chevronL" size={16} stroke={2.2} />
        </button>
        <input type="date" aria-label={t('Journée')} value={iso} max={maxIso}
          onChange={(e) => { const v = e.target.value.replace(/-/g, ''); if (v.length === 8 && v <= today) onChoisir(v); }}
          style={{ ...CONTROLE, padding: '0 11px', color: 'var(--ink-900)', fontSize: '13.5px', fontWeight: 600, outline: 'none', fontVariantNumeric: 'tabular-nums' }} />
        <button type="button" onClick={() => onDecaler(1)} aria-label={t('Jour suivant')} disabled={cEst}
          style={{ ...CONTROLE, width: 40, padding: 0, color: cEst ? 'var(--ink-300)' : 'var(--ink-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: cEst ? 'default' : 'pointer' }}>
          <Icon name="chevronR" size={16} stroke={2.2} />
        </button>
      </div>
      <span style={{ flex: 1, minWidth: 150, fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-800)' }}>
        {jourEnToutesLettres(day)}
      </span>
      {!cEst ? (
        <button type="button" onClick={onAujourdhui}
          style={{ ...CONTROLE, padding: '0 14px', color: 'var(--ink-800)', fontSize: 'var(--t-lead)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer' }}>
          
          {t('Aujourd’hui')}
        </button>
      ) : null}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 40, padding: '0 13px', borderRadius: 'var(--r-sm)', background: ctx.bg, color: ctx.fg, fontSize: '12.5px', fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap' }}>
        <Icon name={ctx.ic} size={14} stroke={2.1} />
        {ctx.l}
      </span>
    </div>
  );
}

/**
 * « mardi 12 août 2026 » — le libellé long, RENDU PAR `Intl`.
 * 🔴 L'ORDRE DES MOTS D'UNE DATE CHANGE AVEC LA LANGUE : « Wednesday, August 12, 2026 » en
 *    anglais, « miércoles, 12 de agosto de 2026 » en espagnol. Une date assemblée à la main par
 *    concaténation ne peut donc jamais être juste ailleurs qu'en français, même avec ses douze mois
 *    traduits — et ce défaut-là ne se voit pas en relisant, il se voit en changeant de langue.
 * ⚠️ LE RENDU FRANÇAIS EST INCHANGÉ à l'octet près, ce que les gardes de format exigent déjà.
 */
export function jourEnToutesLettres(ymd: string): string {
  const d = new Date(Number(ymd.slice(0, 4)), Number(ymd.slice(4, 6)) - 1, Number(ymd.slice(6, 8)));
  return new Intl.DateTimeFormat(currentLang(), { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(d);
}

/**
 * UNE CAISSE, UNE CARTE — a-t-elle fermé, combien a-t-elle encaissé, son tiroir tombe-t-il juste.
 *
 * ⚠️ « CAISSE ENCORE OUVERTE » EST UNE RÉPONSE, pas un trou : tant qu'aucun comptage de clôture n'est
 * remonté, l'écart n'existe pas et s'affiche « — ». Afficher 0,00 € laisserait croire que le tiroir a
 * été compté et qu'il tombait juste.
 */
function CarteCaisse({ r, onOuvrirZ, revolue }: { r: CashRegisterDay; onOuvrirZ: () => void; revolue: boolean }) {
  const etat = etatDeCaisse(r, revolue);
  const close = r.closedAtFiscal !== null;
  // ⚠️ SEULE UNE CAISSE CLÔTURÉE S'OUVRE, comme la maquette : une caisse encore ouverte n'a pas de Z,
  //    et un clic qui n'ouvrirait rien ferait douter de tous les autres.
  // ⚠️ TOUTE SESSION S'OUVRE, close ou non — c'est un écart ASSUMÉ avec la maquette, qui ne rend
  //    cliquables que les caisses clôturées. Une caisse en cours de journée a déjà un fond, un
  //    comptage d'ouverture et des prélèvements à montrer, et c'est celle sur laquelle on se pose des
  //    questions maintenant. Une caisse SANS session, elle, n'a rien : elle reste inerte.
  const ouvrable = r.sessionId !== null;
  const variance = r.varianceCents;
  // ⚠️ RIEN À JUSTIFIER SANS COMPTAGE : un écart qui n'a pas été MESURÉ ne s'explique pas, et demander
  //    un motif dessus ferait signer une explication pour un fait inexistant.
  // ⚠️ UNE CAISSE DE REPRISE NE SE JUSTIFIE PAS : son écart vient d'un logiciel qui l'a déjà traité.
  const aJustifier = close && !r.autoClosed && !r.reprise && variance !== null && variance !== 0 && !r.justification;

  return (
    <div onClick={ouvrable ? onOuvrirZ : undefined}
      role={ouvrable ? 'button' : undefined} tabIndex={ouvrable ? 0 : undefined}
      onKeyDown={ouvrable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOuvrirZ(); } } : undefined}
      style={{ background: 'var(--paper)', border: `1px solid ${aJustifier ? 'var(--amber)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-md)', overflow: 'hidden', cursor: ouvrable ? 'pointer' : 'default' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px', borderBottom: '1px solid var(--ink-200)', background: etat.fond }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 8px', borderRadius: 'var(--r-xs)', background: 'var(--paper)', border: '1px solid var(--ink-200)', color: 'var(--ink-700)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', fontWeight: 700, flexShrink: 0 }}>
          {codeCaisse(r.registerId)}
        </span>
        <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ display: 'flex', color: etat.teinte, flexShrink: 0 }}>
            <Icon name={etat.icone} size={15} stroke={2.1} />
          </span>
          <span style={{ fontSize: '12.5px', fontWeight: 700, color: etat.teinte, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {etat.displayLabel}
          </span>
        </span>
        {/* ⚠️ L'AFFORDANCE, ET PAS SEULEMENT LE CURSEUR. Une carte cliquable dont rien ne le dit n'est
            pas cliquable : personne ne promène sa souris sur un tableau de bord pour découvrir ce qui
            réagit. Le chevron n'apparaît que si la carte s'ouvre vraiment — sinon il promettrait. */}
        {ouvrable ? (
          <span style={{ display: 'flex', color: etat.teinte, flexShrink: 0, opacity: .7 }}>
            <Icon name="chevronR" size={15} stroke={2.2} />
          </span>
        ) : null}
      </div>

      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 11 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: '10.5px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>{t('Encaissé')}</span>
            <span style={{ display: 'block', fontSize: 'var(--t-h2)', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>
              {formatMoney(r.netCents, { thousandsSep: ' ' })}
            </span>
            {/* ⚠️ LES TITRES VENDUS SE DISENT À PART, comme au Z : encaissés, hors chiffre d'affaires — sans cette ligne,
                les règlements du jour dépasseraient l'encaissé sans explication. */}
            {r.titlesIssuedCents !== 0 && (
              <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>
                {t('+ {v} de titres émis (hors CA)', { v: formatMoney(r.titlesIssuedCents, { thousandsSep: ' ' }) })}
              </span>
            )}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 8px', borderRadius: 'var(--r-xs)', background: 'var(--ink-100)', color: 'var(--ink-700)', fontSize: '11.5px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', flexShrink: 0, whiteSpace: 'nowrap' }}>
            {t('{n} pièce', { n: r.pieces })}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 }}>
          <Encart label={t('Espèces théorique')} valeur={r.expectedCashCents === null ? '—' : formatMoney(r.expectedCashCents, { thousandsSep: ' ' })} />
          {/* ⚠️ UNE CLÔTURE AUTOMATIQUE N'A PAS D'ÉCART, elle a une ABSENCE DE COMPTAGE. Afficher son
              « 0,00 € » en vert dirait « tiroir vérifié, tombe juste » alors que personne n'a ouvert le
              tiroir. Le tiret dit la vérité : il n'y a rien à comparer. */}
          <Encart label={r.autoClosed ? t('Tiroir non compté') : t('Écart constaté')}
            valeur={variance === null || r.autoClosed ? '—' : ecartSigne(variance)}
            fond={!r.autoClosed && variance !== null && variance !== 0 ? 'var(--amber-bg)' : 'var(--ink-50)'}
            teinte={variance === null || r.autoClosed ? 'var(--ink-400)' : variance === 0 ? 'var(--teal-800)' : 'var(--amber-fg)'} />
        </div>

        {/* ⚠️ LES DEUX ANOMALIES S'EXPLIQUENT SUR LA CARTE, pas dans une modale : ce sont elles qui font
            douter de tout le reste des chiffres si personne ne les nomme. */}
        {r.sessionId === null && r.pieces > 0 ? (
          <Etiquette tone="amber" icon="alert"
            texte={t('Cette caisse a encaissé, mais aucune session n’est rattachée à cette journée')} />
        ) : r.autoClosed ? (
          <Etiquette tone="amber" icon="alert"
            texte={r.closedAtFiscal
              ? t('Fermée par la caisse le {date} à {heure} — sans comptage du tiroir', { date: `${r.closedAtFiscal.slice(6, 8)}/${r.closedAtFiscal.slice(4, 6)}`, heure: heureDe(r.closedAtFiscal) })
              : t('Fermée par la caisse le lendemain — sans comptage du tiroir')} />
        ) : null}

        {r.justification ? (
          <Etiquette tone="teal" icon="check" texte={t('Écart justifié — {motifCode}', { motifCode: r.justification.motifCode })} />
        ) : aJustifier ? (
          <Etiquette tone="amber" icon="alert" texte={t('Écart à justifier avant la clôture du mois')} />
        ) : null}

        {/* ⚠️ LES HORAIRES DE LA SESSION, JAMAIS TRONQUÉS. Savoir qu'une caisse a clôturé à 19h42 ne dit
            pas si elle a ouvert à 8h ou à 15h — et c'est cet écart qui fait lire un chiffre d'affaires.
            ⚠️ Absents = AUCUNE SESSION en base, pas « horaires inconnus » : la ligne disparaît plutôt que
            d'afficher deux tirets qui laisseraient croire à une donnée perdue. */}
        {r.openedAtFiscal ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 'var(--t-dense)', color: 'var(--ink-600)', fontVariantNumeric: 'tabular-nums' }}>
            <Icon name="clock" size={13} stroke={2} />
            
            {r.closedAtFiscal
              ? t('Ouverte à {v} → clôturée à {v2}', { v: heureDe(r.openedAtFiscal), v2: heureDe(r.closedAtFiscal) })
              : t('Ouverte à {v} — toujours en cours', { v: heureDe(r.openedAtFiscal) })}
          </span>
        ) : null}

        {/* ⚠️ LE LIBELLÉ DIT CE QU'ON VA VOIR, et il n'est pas le même selon l'état : sur une caisse
            ouverte on vient chercher l'ouverture et le tiroir, sur une caisse clôturée on vient
            chercher le Z. « Voir le détail » aurait été vrai et inégal. */}
        {ouvrable ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-500)' }}>
            <Icon name="eye" size={13} stroke={2} />
            {close ? t('Ouverture, tiroir et Z') : t('Ouverture et tiroir')}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** « 19:42 » — l'heure seule d'un horodatage fiscal, quand la date est déjà donnée par le navigateur. */
export function heureDe(dt: string | null): string {
  return dt && dt.length === 14 ? `${dt.slice(8, 10)}:${dt.slice(10, 12)}` : '—';
}

/** Un petit bloc étiquette + valeur du corps de carte. */
function Encart({ label, valeur, fond = 'var(--ink-50)', teinte = 'var(--ink-800)' }: {
  label: string; valeur: string; fond?: string; teinte?: string;
}) {
  return (
    <span style={{ padding: '9px 11px', borderRadius: 'var(--r-ctrl)', background: fond }}>
      <span style={{ display: 'block', fontSize: '10.5px', color: 'var(--ink-500)' }}>{label}</span>
      <span style={{ display: 'block', fontSize: '13.5px', fontWeight: 700, color: teinte, fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>{valeur}</span>
    </span>
  );
}

/** La bande colorée de bas de carte — ce qu'il reste à faire, ou ce qui a été fait. */
function Etiquette({ tone, icon, texte }: { tone: 'teal' | 'amber'; icon: IconName; texte: string }) {
  const bg = tone === 'teal' ? 'var(--teal-100)' : 'var(--amber-bg)';
  const fg = tone === 'teal' ? 'var(--teal-800)' : 'var(--amber-fg)';
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 11px', borderRadius: 'var(--r-ctrl)', background: bg }}>
      <span style={{ display: 'flex', color: fg, flexShrink: 0 }}><Icon name={icon} size={14} stroke={2.1} /></span>
      <span style={{ fontSize: 'var(--t-dense)', fontWeight: 600, color: fg, minWidth: 0 }}>{texte}</span>
    </span>
  );
}

/** ⚠️ LE SIGNE EST L'INFORMATION : « 2,50 € » ne dit pas si le tiroir est trop plein ou trop vide.
 *  Le moins est un vrai signe moins (U+2212), pas un trait d'union — il s'aligne sur les chiffres. */
export function ecartSigne(cents: number): string {
  if (cents === 0) return formatMoney(0, { thousandsSep: ' ' });
  return (cents > 0 ? '+' : '−') + formatMoney(Math.abs(cents), { thousandsSep: ' ' });
}

/**
 * LES ÉCARTS DE LA JOURNÉE — un par caisse dont le tiroir ne tombe pas juste, avec de quoi l'expliquer.
 *
 * ⚠️ JUSTIFIER N'EST PAS CORRIGER, et l'écran le dit en toutes lettres en pied de bloc : le montant
 * compté reste celui que la caisse a signé. Aucun champ de montant n'est offert ici — en offrir un
 * ferait croire que le back-office peut réécrire un comptage.
 */
function SectionEcarts({ variances, motifs, onEnregistre }: {
  variances: CashRegisterDay[];
  motifs: { code: string; label: string }[];
  onEnregistre: () => void;
}) {
  // ⚠️ UN ÉCART EST CELUI D'UN COMPTAGE, jamais d'une caisse : une caisse rouverte dans la journée en porte deux, et les
  //    repérer par la caisse ouvrait les deux formulaires d'un clic.
  const [ouvert, setOuvert] = useState<string | null>(null);
  const restants = variances.filter((e) => !e.justification).length;

  return (
    <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 170, fontSize: '14.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Écarts de caisse')}</span>
        <span style={{ fontSize: '12.5px', color: 'var(--ink-500)', flexShrink: 0 }}>
          {restants === 0 ? t('tous justifiés') : t('{restants} à justifier', { restants })}
        </span>
      </div>

      {variances.map((e) => (
        <LigneEcart key={e.varianceCountId ?? e.registerId} e={e} motifs={motifs}
          ouvert={ouvert === (e.varianceCountId ?? e.registerId)}
          onOpen={() => setOuvert(e.varianceCountId ?? e.registerId)}
          onClose={() => setOuvert(null)}
          onEnregistre={() => { setOuvert(null); onEnregistre(); }} />
      ))}

      <div style={{ padding: '14px 18px', background: 'var(--ink-50)', fontSize: '12.5px', color: 'var(--ink-600)', lineHeight: 1.6 }}>
        <Trans k='<1>Justifier n’est pas corriger.</1> Le montant compté reste celui que la caisse a signé — la justification s’ajoute à côté, elle ne remplace rien. Un écart <2>non justifié bloque la clôture du mois</2> : c’est la seule contrainte que cet écran impose.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
    </div>
  );
}

/** Une caisse en écart : le montant, son explication s'il y en a une, le formulaire sinon. */
function LigneEcart({ e, motifs, ouvert, onOpen, onClose, onEnregistre }: {
  e: CashRegisterDay;
  motifs: { code: string; label: string }[];
  ouvert: boolean; onOpen: () => void; onClose: () => void; onEnregistre: () => void;
}) {
  const variance = e.varianceCents ?? 0;
  const trop = variance > 0;

  return (
    <div style={{ borderBottom: '1px solid var(--ink-100)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '52px minmax(0,1fr) 108px 132px', gap: 12, padding: '12px 18px', alignItems: 'center' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 8px', borderRadius: 'var(--r-xs)', background: 'var(--ink-100)', color: 'var(--ink-700)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', fontWeight: 700 }}>
          {codeCaisse(e.registerId)}
        </span>
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-900)' }}>
            {trop ? t('Excédent en caisse') : t('Manquant en caisse')}
          </span>
          <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 2 }}>
            
            {t('Compté à {v} · théorique {v2}', { v: heureDe(e.closedAtFiscal), v2: e.expectedCashCents === null ? '—' : formatMoney(e.expectedCashCents, { thousandsSep: ' ' }) })}
          </span>
        </span>
        <span style={{ fontSize: 'var(--t-h4)', fontWeight: 800, color: 'var(--amber-fg)', fontVariantNumeric: 'tabular-nums' }}>
          {ecartSigne(variance)}
        </span>
        <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {e.justification ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 36, padding: '0 12px', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-100)', color: 'var(--teal-800)', fontSize: '12.5px', fontWeight: 700, whiteSpace: 'nowrap' }}>
              <Icon name="check" size={13} stroke={2.6} />{t('Justifié')}
            </span>
          ) : ouvert ? null : (
            <button type="button" onClick={onOpen}
              style={{ height: 36, padding: '0 13px', border: '1px solid var(--amber)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--amber-fg)', fontSize: '12.5px', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer' }}>
              
              {t('Justifier')}
            </button>
          )}
        </span>
      </div>

      {e.justification ? (
        <div style={{ padding: '0 18px 13px 18px' }}>
          <div style={{ padding: '11px 13px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', fontSize: '12.5px', color: 'var(--ink-700)', lineHeight: 1.55 }}>
            <strong style={{ color: 'var(--ink-900)' }}>{e.justification.motifCode}</strong>
            {e.justification.note ? ` — ${e.justification.note}` : null}
            <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--ink-500)', marginTop: 4 }}>
              {e.justification.author} · {horodatageFiscal(horodatageDe(e.justification.createdAt))}
            </span>
          </div>
        </div>
      ) : null}

      {ouvert && !e.justification ? (
        <FormulaireJustification countId={e.varianceCountId} caisse={codeCaisse(e.registerId)} motifs={motifs} onClose={onClose} onEnregistre={onEnregistre} />
      ) : null}
    </div>
  );
}

/** ⚠️ L'HEURE D'UNE JUSTIFICATION EST CELLE DU BACK-OFFICE, pas d'une caisse : c'est un acte web,
 *  horodaté en epoch par le serveur, et rendu ici dans le fuseau de qui le relit — ce qui est juste,
 *  puisque c'est un humain du back-office qui l'a posé. */
function horodatageDe(epochMs: number): string {
  const d = new Date(epochMs);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}${p(d.getHours())}${p(d.getMinutes())}00`;
}

/**
 * LE FORMULAIRE DE JUSTIFICATION — un motif du référentiel, une précision libre.
 *
 * ⚠️ LES MOTIFS VIENNENT DU SERVEUR : c'est le référentiel du commerçant. Une liste en dur aurait
 * proposé des motifs qu'il a supprimés et caché ceux qu'il a créés.
 * ⚠️ RÉFÉRENTIEL VIDE = PAS DE FORMULAIRE, mais un message qui dit où aller le remplir. Offrir un menu
 * vide serait offrir un bouton qui ne peut pas aboutir (§23.0).
 */
function FormulaireJustification({ countId, caisse, motifs, onClose, onEnregistre }: {
  countId: string | null;
  /** La caisse en écart — elle nomme les champs : le formulaire est rendu par ligne, et « Motif » seul ne dit pas laquelle. */
  caisse: string;
  motifs: { code: string; label: string }[];
  onClose: () => void; onEnregistre: () => void;
}) {
  const [reason, setMotif] = useState('');
  const [note, setNote] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  if (motifs.length === 0) {
    return (
      <div style={{ padding: '0 18px 14px 18px' }}>
        <div style={{ padding: '11px 13px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', fontSize: '12.5px', color: 'var(--ink-700)', lineHeight: 1.55 }}>
          <Trans k='Aucun motif d’écart de caisse n’est encore défini pour ce magasin. Les motifs se règlent dans les référentiels, sous <1>Justifications</1> — sans eux, une justification n’aurait rien à nommer.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
      </div>
    );
  }

  const save = () => {
    // ⚠️ SANS COMPTAGE, PAS D'ANCRAGE : une justification se rattache au comptage qui porte l'écart.
    //    Ce cas ne devrait pas s'afficher — s'il s'affiche, il le DIT plutôt que d'échouer en silence.
    if (!countId) { setFailure(t('Ce comptage n’a pas d’identifiant : impossible d’y rattacher une justification.')); return; }
    setEnvoi(true);
    setFailure(null);
    api.saveCashVarianceNote(countId, reason, note)
      .then(onEnregistre)
      .catch((e: unknown) => { setFailure(e instanceof Error ? e.message : String(e)); setEnvoi(false); });
  };

  return (
    <div style={{ padding: '0 18px 14px 18px', display: 'flex', flexDirection: 'column', gap: 9 }}>
      <select aria-label={t('Motif de l’écart — caisse {caisse}', { caisse })} value={reason} onChange={(e) => setMotif(e.target.value)} disabled={envoi}
        style={{ height: 38, padding: '0 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-lead)', outline: 'none' }}>
        <option value="">{t('Choisir un motif…')}</option>
        {motifs.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
      </select>
      <input value={note} onChange={(e) => setNote(e.target.value)} disabled={envoi}
        aria-label={t('Précision de l’écart — caisse {caisse}', { caisse })}
        placeholder={t('Précision — ce que la comptabilité lira dans six mois')}
        style={{ height: 38, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-lead)', outline: 'none' }} />
      {failure ? (
        <span style={{ fontSize: '12.5px', color: 'var(--rose-fg)', lineHeight: 1.5 }}>{failure}</span>
      ) : null}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" onClick={save} disabled={!reason || envoi}
          style={{ height: 38, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)', background: reason && !envoi ? 'var(--teal-500)' : 'var(--ink-100)', color: reason && !envoi ? '#fff' : 'var(--ink-400)', fontSize: 'var(--t-lead)', fontWeight: 700, cursor: reason && !envoi ? 'pointer' : 'default' }}>
          {envoi ? t('Enregistrement…') : t('Enregistrer la justification')}
        </button>
        <button type="button" onClick={onClose} disabled={envoi}
          style={{ height: 38, padding: '0 13px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-lead)', fontWeight: 600, cursor: 'pointer' }}>
          
          {t('Annuler')}
        </button>
      </div>
    </div>
  );
}

/**
 * LA VENTILATION TVA DU JOUR — ce que les pièces ont scellé, taux par taux.
 *
 * ⚠️ ELLE PORTE SUR LES MÊMES PIÈCES QUE LES CARTES : le serveur en a une seule définition. Deux
 * filtres auraient fini par afficher deux encaissés du même jour.
 */
function VentilationTva({ rows, total, titles }: { rows: CashDayView['vat']; total: number; titles: number }) {
  return (
    <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
      <div style={{ padding: '13px 17px', borderBottom: '1px solid var(--ink-200)', fontSize: 'var(--t-control)', fontWeight: 700, color: 'var(--ink-900)' }}>
        
        {t('Ventilation TVA du jour')}
      </div>
      {rows.length === 0 ? (
        <div style={{ padding: '18px 17px', fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Aucune pièce ce jour-là.')}</div>
      ) : rows.map((l) => (
        <div key={l.rateBp} style={{ display: 'grid', gridTemplateColumns: '62px minmax(0,1fr) 84px 84px', gap: 8, padding: '10px 17px', alignItems: 'center', borderBottom: '1px solid var(--ink-100)', fontSize: '12.5px', fontVariantNumeric: 'tabular-nums' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--ink-900)' }}>{tauxLisible(l.rateBp)}</span>
          <span style={{ color: 'var(--ink-600)' }}>{formatMoney(l.baseExclCents, { thousandsSep: ' ' })}</span>
          <span style={{ textAlign: 'right', color: 'var(--ink-600)' }}>{formatMoney(l.taxCents, { thousandsSep: ' ' })}</span>
          <span style={{ textAlign: 'right', fontWeight: 700, color: 'var(--ink-900)' }}>
            {formatMoney(l.baseExclCents + l.taxCents, { thousandsSep: ' ' })}
          </span>
        </div>
      ))}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 17px', background: 'var(--ink-50)' }}>
        <span style={{ flex: 1, fontSize: '12.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Total encaissé')}</span>
        <span style={{ fontSize: 'var(--t-h4)', fontWeight: 800, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(total, { thousandsSep: ' ' })}
        </span>
      </div>
      {titles !== 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 17px', borderTop: '1px solid var(--ink-100)', fontSize: '12.5px', color: 'var(--ink-600)', fontVariantNumeric: 'tabular-nums' }}>
          <span style={{ flex: 1 }}>{t('Titres émis (hors CA)')}</span>
          <span>{formatMoney(titles, { thousandsSep: ' ' })}</span>
        </div>
      )}
    </div>
  );
}

/** « 20,0 % » — le taux en points de base rendu lisible, sans décimale inutile sur les taux entiers. */
export function tauxLisible(bp: number): string {
  return formatRateBp(bp);
}

/**
 * LES RÈGLEMENTS DU JOUR — par quoi les clients ont payé, et dans quelle proportion.
 *
 * ⚠️ LA PART SE CALCULE SUR LE TOTAL DES RÈGLEMENTS, pas sur l'encaissé : un client qui règle en deux
 * fois produit deux lignes de règlement pour une seule pièce, et rapporter chaque moyen à l'encaissé
 * aurait donné des parts qui ne font pas 100 %.
 */
function ReglementsDuJour({ rows, total }: { rows: CashDayView['payments']; total: number }) {
  const sum = rows.reduce((sum, l) => sum + l.amountCents, 0);
  return (
    <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
      <div style={{ padding: '13px 17px', borderBottom: '1px solid var(--ink-200)', fontSize: 'var(--t-control)', fontWeight: 700, color: 'var(--ink-900)' }}>
        
        {t('Règlements du jour')}
      </div>
      {rows.length === 0 ? (
        <div style={{ padding: '18px 17px', fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Aucun règlement ce jour-là.')}</div>
      ) : rows.map((l) => {
        const meta = REGLEMENTS[l.methodCode];
        const part = sum > 0 ? pctBp(l.amountCents, sum) / 100 : 0;
        return (
          <div key={l.methodCode} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 17px', borderBottom: '1px solid var(--ink-100)' }}>
            <span style={{ width: 28, height: 28, borderRadius: 'var(--r-ctrl)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', color: meta?.tone ?? 'var(--ink-500)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon name={meta?.icon ?? 'wallet'} size={15} stroke={1.9} />
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {libelleReglement(l.methodCode)}
              </span>
              <span style={{ display: 'block', height: 4, borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', marginTop: 5, overflow: 'hidden' }}>
                <span style={{ display: 'block', height: 4, width: `${part}%`, borderRadius: 'var(--r-pill)', background: meta?.tone ?? 'var(--ink-500)' }} />
              </span>
            </span>
            <span style={{ textAlign: 'right', flexShrink: 0 }}>
              <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>
                {formatMoney(l.amountCents, { thousandsSep: ' ' })}
              </span>
              <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>
                {part.toFixed(0)} %
              </span>
            </span>
          </div>
        );
      })}
      <div style={{ padding: '12px 17px', background: 'var(--ink-50)', fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.55 }}>
        {/* ⚠️ LA MAQUETTE ANNONCE, SUR UNE JOURNÉE PASSÉE, DES TOTAUX « LUS DANS LE Z TRANSMIS ». Ce
            n'est pas ce que fait le back-office : il ne conserve pas d'instantané de Z, il recompte les
            pièces — pour hier comme pour aujourd'hui. Reprendre la phrase de la maquette aurait décrit
            une mécanique qui n'existe pas (§23.0). */}
        
        {t('Ces totaux sont ceux du journal des ventes, recalculés depuis les pièces scellées — pas une seconde comptabilité. Les avoirs y sont déduits, les ventes annulées exclues.')}
        {sum !== total ? (
          <>
            {t('⚠️ La somme des règlements ({v}) diffère de l’encaissé : un écart entre les deux vient de règlements enregistrés sur des pièces sans montant net, et se lit dans le journal.', { v: formatMoney(sum, { thousandsSep: ' ' }) })}</>
        ) : null}
      </div>
    </div>
  );
}

/**
 * LES SESSIONS QUE LA CAISSE N'A PAS ENCORE DATÉES — dites, plutôt que tues.
 *
 * ⚠️ SANS JOURNÉE FISCALE, UNE SESSION EST INVISIBLE SUR TOUTES LES JOURNÉES. La passer sous silence
 * laisserait croire qu'une caisse n'a jamais ouvert ; ce bandeau existe pour que personne ne conclue
 * ça d'un écran vide. Il disparaît de lui-même : la caisse date ses sessions à leur prochain envoi.
 */
function SessionsNonDatees({ n }: { n: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 17px', borderRadius: 'var(--r-md)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', marginTop: 16 }}>
      <span style={{ display: 'flex', flexShrink: 0, color: 'var(--ink-500)', paddingTop: 1 }}>
        <Icon name="info" size={18} stroke={2} />
      </span>
      <span style={{ flex: 1, minWidth: 210, fontSize: '12.5px', color: 'var(--ink-700)', lineHeight: 1.55 }}>
        <strong style={{ color: 'var(--ink-900)' }}>
          {/* ⚠️ UNE CLÉ COMPTÉE, PAS UN TERNAIRE : choisir la forme à la main ne marche que pour les
              langues à DEUX formes, et livre au traducteur deux phrases qu'il ne sait pas liées.
              Le catalogue porte l'accord, et `Intl.PluralRules` choisit pour chaque langue. */}
          {t('{n} sessions de caisse ne sont rattachées à aucune journée', { n })}
        </strong>
        {/* ⚠️ LA CONDITION EST DITE, PAS TUE. « Elles se dateront au prochain envoi » était faux tant que
            la tablette n'a pas la version qui écrit la journée fiscale : c'est exactement le « différé
            sans mécanisme de différé » que le §23.0 interdit. Sans cette phrase, on attendrait une
            convergence qui n'arriverait jamais. */}
        <span style={{ display: 'block', marginTop: 3 }}>
          <Trans k='Elles ont été ouvertes avant que les caisses ne datent leur tiroir en heure de magasin, et n’apparaissent donc sur aucune journée — leur carte reste alors <1>non cliquable</1>. Elles se dateront seules dès que la caisse concernée tournera avec une version de l’application qui écrit la journée fiscale, puis enverra ses données.' parts={[(x) => <strong>{x}</strong>]} /></span>
      </span>
    </div>
  );
}

/**
 * « juillet 2026 » depuis un `AAAAMM` — RENDU PAR `Intl`, pour la même raison que le jour.
 * ⚠️ La table qui vivait ici était le JUMEAU EXACT de celle du haut du fichier (Forme A) : deux
 *    copies du même calendrier, dans le même écran.
 */
export function moisEnToutesLettres(ym: string): string {
  const m = Number(ym.slice(4, 6));
  if (!(m >= 1 && m <= 12)) return `${ym.slice(4, 6)} ${ym.slice(0, 4)}`;
  return new Intl.DateTimeFormat(currentLang(), { month: 'long', year: 'numeric' }).format(new Date(Number(ym.slice(0, 4)), m - 1, 1));
}

/**
 * L'ONGLET « CLÔTURES MENSUELLES » — ce qui bloque le prochain scellement, puis le relevé des mois.
 *
 * ⚠️ AUCUN BOUTON « CLÔTURER » ICI, et c'est la nature de la chose : une clôture mensuelle est une
 * pièce SIGNÉE (`fiscal_grand_total` de type `GTM`), et seule une caisse détient la clé qui la scelle.
 * Le back-office CONSTATE qu'elle existe ou qu'elle manque. Offrir le bouton de la maquette aurait
 * promis une opération que rien ne peut exécuter — et sur un écran fiscal, c'est la pire des promesses.
 */
function OngletMensuel({ vue, onVoirJournee }: { vue: CashMonthsView | null; onVoirJournee: (day: string) => void }) {
  // ⚠️ UN SEUL MOIS DÉPLIÉ À LA FOIS : chaque dépliage déclenche une lecture serveur, et en laisser
  //    plusieurs ouverts ferait payer à chaque clic la relecture de tout ce qui précède.
  const [deplie, setDeplie] = useState<string | null>(null);
  if (!vue) {
    return <EmptyState icon="calendar" title={t('Relevé mensuel indisponible')}
      note={t('Les mois de ce magasin n’ont pas pu être lus. Rechargez l’écran ; si cela persiste, c’est la base du magasin qui ne répond pas.')} />;
  }
  if (vue.months.length === 0) {
    return <EmptyState icon="calendar" title={t('Aucun mois d’exploitation')}
      note={t('Aucune vente n’a encore été scellée dans ce magasin — il n’y a donc rien à clôturer.')} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {vue.blockingYm ? <CarteBlocages ym={vue.blockingYm} blocages={vue.blockers} onVoirJournee={onVoirJournee} /> : null}

      <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 640 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 84px 108px 100px 108px 132px 26px', gap: 10, padding: '11px 18px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)', fontSize: '10.5px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
              <div>{t('Période')}</div>
              <div style={{ textAlign: 'right' }}>{t('Pièces')}</div>
              <div style={{ textAlign: 'right' }}>{t('Encaissé')}</div>
              <div style={{ textAlign: 'right' }}>{t('TVA due')}</div>
              <div style={{ textAlign: 'right' }}>{t('Écarts')}</div>
              <div>{t('État')}</div>
              <div />
            </div>
            {vue.months.map((m) => (
              <LigneMois key={m.ym} m={m}
                ouvert={deplie === m.ym}
                onBasculer={() => setDeplie(deplie === m.ym ? null : m.ym)} />
            ))}
          </div>
        </div>
        {/* ⚠️ LA MAQUETTE RENVOIE À « ARCHIVES FISCALES », écran qui n'existe pas encore : la phrase est
            gardée, le renvoi cliquable non — un lien mort vaut moins qu'une phrase qui nomme le sujet. */}
        <div style={{ padding: '13px 18px', background: 'var(--ink-50)', fontSize: '12.5px', color: 'var(--ink-600)', lineHeight: 1.6 }}>
          <Trans k='Un mois clôturé est <1>verrouillé, pas archivé</1> : il reste consultable ici et dans le journal. L’archivage — et la question de ce qui reste interrogeable au bout de six ans — est le sujet des archives fiscales.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
      </div>
    </div>
  );
}

/** Une ligne du relevé mensuel : ce que pèse le mois, et ce qui l'empêche d'être scellé. */
function LigneMois({ m, ouvert, onBasculer }: { m: CashMonth; ouvert: boolean; onBasculer: () => void }) {
  const etat = m.sealed
    ? { l: t('Clôturé'), bg: 'var(--ink-100)', fg: 'var(--ink-600)', ic: 'lock' as IconName }
    : m.missingZ > 0 || m.unjustified > 0
      ? { l: t('Bloqué'), bg: 'var(--amber-bg)', fg: 'var(--amber-fg)', ic: 'alert' as IconName }
      : { l: t('À clôturer'), bg: 'var(--teal-100)', fg: 'var(--teal-800)', ic: 'check' as IconName };

  return (
    <>
    <div onClick={onBasculer} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onBasculer(); } }}
      style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 84px 108px 100px 108px 132px 26px', gap: 10, padding: '0 18px', minHeight: 48, alignItems: 'center', borderBottom: '1px solid var(--ink-100)', fontSize: '13.5px', cursor: 'pointer' }}>
      <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ display: 'flex', color: etat.fg, flexShrink: 0 }}><Icon name={etat.ic} size={15} stroke={2} /></span>
        <span style={{ fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {moisEnToutesLettres(m.ym)}
        </span>
      </div>
      <div style={{ textAlign: 'right', fontSize: '12.5px', color: 'var(--ink-600)', fontVariantNumeric: 'tabular-nums' }}>{m.pieces}</div>
      <div style={{ textAlign: 'right', fontWeight: 700, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>
        {formatMoney(m.netCents, { thousandsSep: ' ' })}
      </div>
      <div style={{ textAlign: 'right', fontSize: '12.5px', color: 'var(--ink-600)', fontVariantNumeric: 'tabular-nums' }}>
        {formatMoney(m.taxCents, { thousandsSep: ' ' })}
      </div>
      {/* ⚠️ « — » ET NON « 0,00 € » quand aucun écart n'a été mesuré : un mois sans comptage de clôture
          n'est pas un mois qui tombe juste, et le zéro le ferait croire. */}
      <div style={{ textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: m.varianceCents > 0 ? 'var(--amber-fg)' : 'var(--ink-400)' }}>
        {m.varianceCents > 0 ? formatMoney(m.varianceCents, { thousandsSep: ' ' }) : '—'}
      </div>
      <div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 24, padding: '0 10px', borderRadius: 'var(--r-pill)', background: etat.bg, color: etat.fg, fontSize: '11.5px', fontWeight: 700, whiteSpace: 'nowrap' }}>
          {etat.l}
        </span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', color: 'var(--ink-400)' }}>
        <Icon name={ouvert ? 'chevronU' : 'chevronD'} size={15} stroke={2} />
      </div>
    </div>
    {ouvert ? <JourParJour ym={m.ym} /> : null}
    </>
  );
}

/**
 * CE QUI EMPÊCHE DE SCELLER LE PROCHAIN MOIS — une ligne par caisse et par journée en cause.
 *
 * ⚠️ LE MOIS VISÉ EST LE PLUS ANCIEN NON SCELLÉ, pas le mois courant : une clôture mensuelle se fait
 * dans l'ordre, et montrer les blocages de septembre pendant que juillet traîne désignerait le
 * mauvais travail à faire.
 */
function CarteBlocages({ ym, blocages, onVoirJournee }: {
  ym: string; blocages: CashBlocker[]; onVoirJournee: (day: string) => void;
}) {
  const rien = blocages.length === 0;
  return (
    <div style={{ background: 'var(--paper)', border: `1px solid ${rien ? 'var(--ink-200)' : 'var(--amber)'}`, borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '15px 18px', background: rien ? 'var(--teal-100)' : 'var(--amber-bg)', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', flexShrink: 0, color: rien ? 'var(--teal-700)' : 'var(--amber-fg)', paddingTop: 1 }}>
          <Icon name={rien ? 'check' : 'alert'} size={19} stroke={2} />
        </span>
        <span style={{ flex: 1, minWidth: 210 }}>
          <span style={{ display: 'block', fontSize: '14.5px', fontWeight: 700, color: 'var(--ink-900)' }}>
            {rien
              ? t('Rien ne bloque {v}', { v: moisEnToutesLettres(ym) })
              : t('{n} point à traiter avant de clôturer {v}', { n: blocages.length, v: moisEnToutesLettres(ym) })}
          </span>
          {/* ⚠️ LE SEUL BOUTON QUE LA MAQUETTE MET ICI EST « CLÔTURER JUILLET 2026 ». Il n'existe pas :
              seule une caisse scelle un GTM. La phrase dit QUI peut le faire, à la place d'un bouton
              qui ne pourrait pas aboutir (§23.0). */}
          <span style={{ display: 'block', fontSize: '12.5px', color: 'var(--ink-700)', marginTop: 3, lineHeight: 1.55 }}>
            <Trans k='La clôture du mois est <1>signée par une caisse</1>, jamais par le back-office — qui constate seulement qu’elle existe ou qu’elle manque.' parts={[(x) => <strong>{x}</strong>]} /></span>
        </span>
      </div>

      {blocages.map((b) => (
        <div key={`${b.day}-${b.registerId}-${b.kind}`} style={{ display: 'grid', gridTemplateColumns: '26px minmax(0,1fr) 150px', gap: 12, padding: '12px 18px', alignItems: 'center', borderBottom: '1px solid var(--ink-100)' }}>
          <span style={{ display: 'flex', color: 'var(--amber-fg)' }}><Icon name="alert" size={16} stroke={2.1} /></span>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-900)' }}>
              {b.kind === 'missing_z'
                ? t('La caisse {v} n’a pas envoyé son Z', { v: codeCaisse(b.registerId) })
                : t('Écart non justifié sur la caisse {v}', { v: codeCaisse(b.registerId) })}
            </span>
            <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 2 }}>
              {jourEnToutesLettres(b.day)}
              {b.varianceCents !== null ? ` · ${ecartSigne(b.varianceCents)}` : null}
            </span>
          </span>
          <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => onVoirJournee(b.day)}
              style={{ height: 36, padding: '0 13px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-800)', fontSize: '12.5px', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer' }}>
              
              {t('Voir la journée')}
            </button>
          </span>
        </div>
      ))}

      <div style={{ padding: '14px 18px', background: 'var(--ink-50)', fontSize: '12.5px', color: 'var(--ink-600)', lineHeight: 1.6 }}>
        <Trans k='La clôture mensuelle est une <1>obligation NF525</1>, et elle est <2>définitive</2> : après elle, aucune pièce du mois ne peut plus être ajoutée ni modifiée. C’est pour ça qu’elle refuse de partir tant qu’un Z manque ou qu’un écart reste sans explication.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
    </div>
  );
}

/**
 * LE JOUR PAR JOUR D'UN MOIS — ce que le dépliage d'une ligne mensuelle montre.
 *
 * ⚠️ IL OCCUPE L'EMPLACEMENT QUE LA MAQUETTE DESTINE AUX « ÉCARTS PAR CAISSE ET MODE DE RÈGLEMENT »,
 * qu'on ne peut pas remplir : seul l'écart ESPÈCES existe (le rapprochement TPE et la remise de
 * chèques n'ont aucune source). Plutôt que de laisser le dépliage vide ou d'inventer deux colonnes,
 * il porte ce qui manquait vraiment — QUEL JOUR et QUELLE CAISSE bloquent le mois. Le relevé dit
 * qu'un mois est bloqué ; ceci montre où.
 *
 * ⚠️ CHARGÉ À L'OUVERTURE, pas avec le relevé : trente jours × N caisses pour chaque mois de
 * l'historique, chargés d'avance, seraient payés par tout le monde pour ce qu'un seul consulte.
 */
function JourParJour({ ym }: { ym: string }) {
  const [days, setJours] = useState<CashMonthDay[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    api.cashMonthDays(ym)
      .then((r) => { if (vivant) setJours(r.days); })
      .catch((e: unknown) => { if (vivant) setFailure(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [ym]);

  if (failure) {
    return <div style={{ padding: '14px 18px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)', fontSize: '12.5px', color: 'var(--rose-fg)' }}>{failure}</div>;
  }
  if (!days) {
    return <div style={{ padding: '14px 18px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)', fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Lecture du mois…')}</div>;
  }
  if (days.length === 0) {
    return <div style={{ padding: '14px 18px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)', fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Aucune journée d’activité dans ce mois.')}</div>;
  }

  return (
    <div style={{ padding: '14px 18px 16px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)' }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 10 }}>
        {t('Clôtures jour par jour — {v}', { v: moisEnToutesLettres(ym) })}
      </div>
      <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden', background: 'var(--paper)' }}>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 560 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '132px minmax(0,1fr) 96px 84px 108px', gap: 10, padding: '10px 16px 8px', background: 'var(--paper)', borderBottom: '1px solid var(--ink-200)', fontSize: 'var(--t-micro)', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ink-400)' }}>
              <div>{t('Journée')}</div>
              <div>{t('Caisses')}</div>
              <div style={{ textAlign: 'right' }}>{t('Encaissé')}</div>
              <div style={{ textAlign: 'right' }}>{t('Pièces')}</div>
              <div style={{ textAlign: 'right' }}>{t('Écart')}</div>
            </div>
            {days.map((j) => <LigneJour key={j.day} j={j} />)}
          </div>
        </div>
      </div>
      <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 9, lineHeight: 1.5 }}>
        <Trans k='Une caisse en <1>ambre</1> n’a pas envoyé son Z, ou porte un écart sans explication. Le détail d’une journée se lit dans l’onglet <2>Journée</2>.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></div>
    </div>
  );
}

/** Une journée du dépliage : ses caisses en pastilles, et ce qu'elle pèse. */
function LigneJour({ j }: { j: CashMonthDay }) {
  // ⚠️ L'ÉCART DU JOUR SE SOMME EN VALEUR ABSOLUE, comme au mois : un manquant et un excédent le même
  //    jour sur deux caisses ne font pas une journée qui tombe juste.
  const mesures = j.registers.filter((r) => r.varianceCents !== null);
  const variance = mesures.reduce((sum, r) => sum + Math.abs(r.varianceCents ?? 0), 0);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '132px minmax(0,1fr) 96px 84px 108px', gap: 10, padding: '0 16px', minHeight: 42, alignItems: 'center', borderBottom: '1px solid var(--ink-100)', fontSize: '12.5px' }}>
      <div style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'var(--ink-900)' }}>
        {`${j.day.slice(6, 8)}/${j.day.slice(4, 6)}/${j.day.slice(2, 4)}`}
      </div>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', minWidth: 0 }}>
        {j.registers.map((r, i) => <PastilleCaisse key={`${r.registerId}-${i}`} r={r} />)}
      </div>
      <div style={{ textAlign: 'right', fontWeight: 700, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>
        {formatMoney(j.netCents, { thousandsSep: ' ' })}
      </div>
      <div style={{ textAlign: 'right', color: 'var(--ink-600)', fontVariantNumeric: 'tabular-nums' }}>{j.pieces}</div>
      {/* ⚠️ « — » = AUCUN COMPTAGE, pas « pas d'écart » : une journée dont aucune caisse n'a compté son
          tiroir n'a rien à comparer, et « 0,00 € » affirmerait qu'elle tombe juste. */}
      <div style={{ textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: variance > 0 ? 'var(--amber-fg)' : 'var(--ink-400)' }}>
        {mesures.length === 0 ? '—' : variance > 0 ? formatMoney(variance, { thousandsSep: ' ' }) : formatMoney(0, { thousandsSep: ' ' })}
      </div>
    </div>
  );
}

/**
 * UNE CAISSE SUR UNE JOURNÉE, en pastille — son code, et ce qui cloche s'il y a lieu.
 *
 * ⚠️ TROIS ÉTATS ET PAS DEUX : clôturée nette, clôturée AVEC un écart sans explication, pas clôturée.
 * Les deux derniers bloquent le mois pour des raisons différentes, et les confondre en un seul
 * « problème » obligerait à ouvrir la journée pour savoir laquelle.
 */
function PastilleCaisse({ r }: { r: CashMonthDayRegister }) {
  const enSouffrance = !r.closed || (r.varianceCents !== null && r.varianceCents !== 0 && !r.justified);
  const heading = !r.closed
    ? t('Z non envoyé')
    : r.varianceCents !== null && r.varianceCents !== 0
      ? (r.justified ? t('Écart {v} — justifié', { v: ecartSigne(r.varianceCents) }) : t('Écart {v} — sans explication', { v: ecartSigne(r.varianceCents) }))
      : t('Z reçu à {v}', { v: r.closedAt ? `${r.closedAt.slice(0, 2)}:${r.closedAt.slice(2, 4)}` : '—' });

  return (
    <span title={heading}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, height: 21, padding: '0 8px', borderRadius: 'var(--r-xs)', background: enSouffrance ? 'var(--amber-bg)' : 'var(--ink-100)', color: enSouffrance ? 'var(--amber-fg)' : 'var(--ink-700)', fontFamily: 'var(--font-mono)', fontSize: '10.5px', fontWeight: 700, whiteSpace: 'nowrap' }}>
      <Icon name={enSouffrance ? 'alert' : 'check'} size={11} stroke={2.4} />
      {codeCaisse(r.registerId)}
    </span>
  );
}

/**
 * LE DÉTAIL D'UN Z — la charge signée par la caisse, telle quelle.
 *
 * ⚠️ RIEN N'EST RECALCULÉ ICI : compteurs, ventilation TVA et cumul perpétuel sortent du grand total
 * SIGNÉ. Les re-dériver depuis les pièces donnerait peut-être les mêmes chiffres — et ce ne seraient
 * plus ceux dont un auditeur peut vérifier la signature.
 *
 * ⚠️ CE QUI N'EST PAS ICI, ET POURQUOI. « Réimprimer le Z » et « Exporter le Z » (maquette) : le
 * duplicata est signé par la caisse, sur son imprimante — le back-office n'a aucun mécanisme, et la
 * note de bas de modale dit précisément pourquoi ça compte. Le bloc « Duplicata émis » non plus :
 * `fiscal_reprint` trace les réimpressions de PIÈCES (`piece_id` → `fiscal_piece`), et un Z n'est pas
 * une pièce — il n'y a donc rien à compter, et un compteur à zéro affirmerait qu'aucun duplicata n'a
 * été tiré alors qu'on n'en sait rien.
 */
function ModaleZ({ registre, onClose }: { registre: CashRegisterDay; onClose: () => void }) {
  const [detail, setDetail] = useState<SessionDetail | null | undefined>(undefined);
  const [failure, setFailure] = useState<string | null>(null);
  /** L'onglet choisi — `null` tant que personne n'a choisi : une session encore ouverte n'a pas de Z, elle s'ouvre sur son tiroir. */
  const [chosenTab, setChosenTab] = useState<'z' | 'drawer' | null>(null);

  useEffect(() => {
    if (!registre.sessionId) return;
    let vivant = true;
    api.sessionDetail(registre.sessionId)
      .then((r) => { if (vivant) setDetail(r.detail); })
      .catch((e: unknown) => { if (vivant) setFailure(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [registre.sessionId]);

  const z = detail?.z ?? null;
  const ouverte = detail != null && detail.closedAtFiscal === null;
  const tab = chosenTab ?? (ouverte ? 'drawer' : 'z');

  // ⚠️ ÉCHAP FERME, comme toute modale du back-office : une modale qu'on ne peut fermer qu'à la souris
  //    piège l'opérateur au clavier, qui est justement celui qui va vite.
  useEffect(() => {
    const surTouche = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [onClose]);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(12,14,16,.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 28 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0 }} />
      {/* ⚠️ La modale s'élargit sur le tiroir (dessin : 760 px) : la table des coupures a six colonnes. */}
      <div role="dialog" aria-modal="true" style={{ position: 'relative', width: tab === 'drawer' ? 760 : 480, transition: 'width .2s', maxWidth: '94vw', height: 'min(90vh,760px)', background: 'var(--paper)', borderRadius: 'var(--r-lg)', boxShadow: '0 28px 70px -10px rgba(0,0,0,.45)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', borderBottom: '1px solid var(--ink-200)', flexShrink: 0 }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-control)', fontWeight: 700, color: 'var(--ink-900)' }}>
              {z ? `Z ${z.zNumber}` : t('Caisse {v}', { v: codeCaisse(registre.registerId) })}
            </span>
            <span style={{ display: 'block', fontSize: '12.5px', color: 'var(--ink-500)', marginTop: 2 }}>
              {z ? horodatageFiscal(z.datetimeFiscal)
                 : ouverte ? t('Ouverte à {v} — toujours en cours', { v: heureDe(detail?.openedAtFiscal ?? null) })
                 : t('Clôture du tiroir à {v}', { v: heureDe(registre.closedAtFiscal) })}
            </span>
          </span>
          <button type="button" onClick={onClose} aria-label={t('Fermer')}
            style={{ width: 32, height: 32, border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', color: 'var(--ink-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0, cursor: 'pointer' }}>
            <Icon name="x" size={15} stroke={2.4} />
          </button>
        </div>

        <div style={{ margin: '10px 20px 0', flexShrink: 0 }}>
          <Segmented options={[{ id: 'z', label: t('Le Z') }, { id: 'drawer', label: t('Le tiroir') }]} value={tab}
            onChange={(k) => setChosenTab(k)} label={t('Le Z ou le tiroir de la caisse')} />
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 13 }}>
          {failure ? (
            <BlocNote tone="rose" texte={failure} />
          ) : detail === undefined ? (
            <BlocNote tone="neutre" texte={t('Lecture de la session…')} />
          ) : detail === null ? (
            <BlocNote tone="ambre" texte={t('Cette session n’existe pas dans la base de ce magasin.')} />
          ) : tab === 'drawer' ? (
            <DrawerTab d={detail} />
          ) : (
            <>
              {/* ⚠️ L'ABSENCE DE Z SE DIT, ET PAS DE LA MÊME FAÇON SELON L'ÉTAT. Session ouverte : c'est
                  normal, le Z vient à la clôture. Session close SANS Z : c'est une anomalie, parce que
                  clôturer le tiroir et sceller le Z sont deux gestes distincts et que le second manque. */}
              {!z ? (
                <BlocNote tone={ouverte ? 'neutre' : 'ambre'}
                  texte={ouverte
                    ? t('Aucun Z fiscal : c’est normal, il est scellé par la caisse à la clôture.')
                    : t('Le tiroir a été clôturé, mais AUCUN Z fiscal n’est remonté pour cette session. Le back-office ne peut pas le reconstituer : un Z ne se rejoue pas, il se redemande à la caisse.')} />
              ) : null}
            </>
          )}
          {z && tab === 'z' ? (
            <>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', flexShrink: 0 }}>
                <span style={{ color: 'var(--ink-500)', flexShrink: 0, display: 'flex', paddingTop: 1 }}><Icon name="lock" size={16} stroke={2} /></span>
                <span style={{ fontSize: '12.5px', color: 'var(--ink-700)', lineHeight: 1.6 }}>
                  <Trans k='Z <1>signé par la caisse {v}</1> et transmis. Le back-office le lit et le conserve — il ne le recalcule pas.' vars={{ v: codeCaisse(z.registerId) }} parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></span>
              </div>

              <BlocEncadre heading={t('Compteurs')}>
                <LigneZ label={t('Compteur perpétuel de pièces')} valeur={String(z.pieceCounter)} />
                <LigneZ label={t('Chiffre d’affaires TTC')} valeur={formatMoney(z.periodInclCents, { thousandsSep: ' ' })} fort />
                <LigneZ label={t('Dont HT')} valeur={formatMoney(z.periodExclCents, { thousandsSep: ' ' })} />
                {z.periodVat.map((v) => (
                  <LigneZ key={v.rateBp} label={t('TVA {v} — base {v2}', { v: tauxLisible(v.rateBp), v2: formatMoney(v.baseExclCents, { thousandsSep: ' ' }) })}
                    valeur={formatMoney(v.taxCents, { thousandsSep: ' ' })} />
                ))}
                {/* ⚠️ LE CUMUL PERPÉTUEL N'EST PAS LE CA DU JOUR : c'est le grand total du registre depuis
                    sa mise en service, qui ne redescend jamais. Les confondre ferait lire un chiffre
                    d'affaires de plusieurs années comme celui d'une soirée. */}
                <LigneZ label={t('Grand total perpétuel du registre')} valeur={formatMoney(z.cumulativeInclCents, { thousandsSep: ' ' })} />
              </BlocEncadre>

              <BlocEncadre heading={t('Empreinte fiscale')}>
                <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 9 }}>
                  <Empreinte label={t('Signature du Z ({signatureAlgo})', { signatureAlgo: z.signatureAlgo })} valeur={z.signature} />
                  {/* ⚠️ LA SIGNATURE PRÉCÉDENTE EST LE CHAÎNAGE : c'est elle qui rend la suite des Z
                      vérifiable, et sans elle la signature seule ne prouve qu'un document isolé. */}
                  <Empreinte label={t('Signature précédente (chaînage)')} valeur={z.prevSignature || '—'} />
                </div>
              </BlocEncadre>
            </>
          ) : null}

          {tab === 'z' ? <div style={{ padding: '12px 14px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', fontSize: '12.5px', color: 'var(--ink-600)', lineHeight: 1.6, flexShrink: 0 }}>
            <Trans k='<1>Réimprimer, oui. Rejouer, non.</1> Un duplicata est une relecture : il ne crée aucune signature et ne remet aucun compteur à zéro. <2>Un Z ne se rejoue pas</2> — s’il manque, on le redemande à la caisse ; on ne le reconstitue pas depuis les tickets, même si les chiffres se recoupent.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div> : null}
        </div>

        {/* ⚠️ UN SEUL BOUTON. La maquette en met trois : « Réimprimer le Z » et « Exporter le Z » sont
            retirés — le duplicata est signé par la caisse, sur son imprimante, et le back-office n'a
            aucun mécanisme derrière ces deux gestes. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 20px', borderTop: '1px solid var(--ink-200)', background: 'var(--ink-50)', flexShrink: 0 }}>
          <div style={{ flex: 1 }} />
          <button type="button" onClick={onClose}
            style={{ height: 38, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-900)', color: 'var(--paper)', fontSize: 'var(--t-lead)', fontWeight: 600, cursor: 'pointer' }}>
            
            {t('Fermer')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Un encadré titré du corps de modale. */
function BlocEncadre({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden', flexShrink: 0 }}>
      <div style={{ padding: '10px 14px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)', fontSize: 'var(--t-meta)', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
        {heading}
      </div>
      {children}
    </div>
  );
}

/** Une ligne « libellé → valeur » d'un compteur de Z. */
function LigneZ({ label, valeur, fort }: { label: string; valeur: string; fort?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 14px', borderBottom: '1px solid var(--ink-100)', fontSize: 'var(--t-lead)' }}>
      <span style={{ flex: 1, minWidth: 0, color: 'var(--ink-600)' }}>{label}</span>
      <span style={{ fontWeight: 700, color: fort ? 'var(--ink-900)' : 'var(--ink-800)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{valeur}</span>
    </div>
  );
}

/** Une empreinte cryptographique — mono, coupée sur toute sa longueur, jamais tronquée.
 *  ⚠️ ELLE SE LIT EN ENTIER OU PAS DU TOUT : une signature abrégée ne permet aucune vérification, et
 *  donnerait l'illusion d'une preuve consultable. */
function Empreinte({ label, valeur }: { label: string; valeur: string }) {
  return (
    <div>
      <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginBottom: 3 }}>{label}</span>
      <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', lineHeight: 1.5, color: 'var(--ink-800)', wordBreak: 'break-all' }}>{valeur}</span>
    </div>
  );
}

/** Un pavé de texte teinté — chargement, échec, ou fait à énoncer. */
function BlocNote({ tone, texte }: { tone: 'neutre' | 'ambre' | 'rose'; texte: string }) {
  const teinte = tone === 'ambre'
    ? { bg: 'var(--amber-bg)', bd: 'var(--amber)', fg: 'var(--ink-800)' }
    : tone === 'rose'
      ? { bg: 'var(--ink-50)', bd: 'var(--ink-200)', fg: 'var(--rose-fg)' }
      : { bg: 'var(--ink-50)', bd: 'var(--ink-200)', fg: 'var(--ink-600)' };
  return (
    <div style={{ padding: '12px 14px', borderRadius: 'var(--r-sm)', background: teinte.bg, border: `1px solid ${teinte.bd}`, fontSize: '12.5px', color: teinte.fg, lineHeight: 1.6, flexShrink: 0 }}>
      {texte}
    </div>
  );
}

const LIBELLE_COMPTAGE: Record<string, Translatable> = {
  opening: key('Ouverture'),
  shift: key('Relève d’équipe'),
  closing: key('Clôture'),
};

const LIBELLE_MOUVEMENT: Record<string, Translatable> = {
  withdrawal: key('Prélèvement'),
  deposit_in: key('Apport'),
  expense: key('Frais'),
};

/**
 * Un comptage du tiroir — ce qui a été compté, ce qui était attendu, et l'écart.
 *
 * ⚠️ L'ÉCART D'OUVERTURE EST AUSSI UN ÉCART : compter 48 € quand l'opérateur déclare un fond de 50 €
 * est une anomalie dès le matin. La maquette ne montre que celui de clôture ; le taire ferait
 * démarrer la journée sur un manquant que personne n'aurait vu.
 * ⚠️ UN TIROIR NON COMPTÉ SE DIT : la caisse y écrit un compté égal à l'attendu, que l'écran ne doit
 * pas présenter comme un comptage juste.
 */
function LigneComptage({ c }: { c: SessionCount }) {
  const variance = c.varianceCents;
  return (
    <div style={{ padding: '9px 14px', borderBottom: '1px solid var(--ink-100)', fontSize: 'var(--t-lead)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', color: 'var(--ink-800)' }}>
            
            {t('Comptage —')} {LIBELLE_COMPTAGE[c.countType] ? t(LIBELLE_COMPTAGE[c.countType]!) : c.countType}
          </span>
          <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 2 }}>
            {heureDe(c.datetimeFiscal)}{c.operator ? ` · ${c.operator}` : ''}  {t('· théorique')} {formatMoney(c.expectedCents, { thousandsSep: ' ' })}
          </span>
        </span>
        {c.uncounted ? (
          <span style={{ textAlign: 'right', flexShrink: 0, fontWeight: 700, color: 'var(--ink-600)' }}>{t('Tiroir non compté')}</span>
        ) : (
          <span style={{ textAlign: 'right', flexShrink: 0 }}>
            <span style={{ display: 'block', fontWeight: 700, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>
              {formatMoney(c.totalCountedCents, { thousandsSep: ' ' })}
            </span>
            <span style={{ display: 'block', fontSize: 'var(--t-note)', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: variance === 0 ? 'var(--teal-800)' : 'var(--amber-fg)' }}>
              {ecartSigne(variance)}
            </span>
          </span>
        )}
      </div>
      {c.note ? (
        <div style={{ marginTop: 6, fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.5 }}>{c.note}</div>
      ) : null}
    </div>
  );
}

/**
 * L'ONGLET « LE TIROIR » DE LA MODALE DU Z — la journée de la caisse, de l'ouverture au reste du soir.
 *
 * ⚠️ RIEN N'EST CALCULÉ ICI QUE LA CAISSE N'AIT ÉCRIT : le théorique est celui de son comptage de clôture, les ventes en
 *    espèces sont DÉDUITES de ce théorique par l'identité de la caisse (serveur, `cashSalesFromCount`), le reste au tiroir
 *    est la règle de la caisse (`remainderAfterDeposits`). Une session encore ouverte n'a ni théorique ni reste : elle le
 *    dit, plutôt que d'additionner un « théorique en cours » que la caisse n'a pas signé.
 * ⚠️ UN COMPTAGE ABSENT OU ILLISIBLE SE DIT dans sa colonne, jamais par des zéros : zéro coupure, c'est un tiroir vide.
 * ⚠️ Gestion, hors NF525 : rien ici n'entre dans la chaîne fiscale, et la note de pied le dit.
 */
export function DrawerTab({ d }: { d: SessionDetail }) {
  const isOpen = d.closedAtFiscal === null;
  const opening = d.counts.find((c) => c.countType === 'opening') ?? null;
  const closings = d.counts.filter((c) => c.countType === 'closing');
  const closing = closings[closings.length - 1] ?? null;
  const shifts = d.counts.filter((c) => c.countType === 'shift');
  const cashDeposits = d.deposits.filter((x) => x.depositType === 'cash');
  const chequeDeposits = d.deposits.filter((x) => x.depositType !== 'cash');
  const depositedCents = cashDeposits.reduce((s, x) => s + x.amountCents, 0);
  const correctionCents = d.carriedOverCents === null ? null : d.openingFloatCents - d.carriedOverCents;
  // ⚠️ CE QUE LES ÉTAPES NE DISENT PAS : l'écart d'une ouverture ou d'une relève (compter 48 € pour un fond de 50 € est une
  //    anomalie dès le matin) et la note qu'un opérateur a laissée sur un comptage.
  const annotated = d.counts.filter((c) => c.note || (c.countType !== 'closing' && !c.uncounted && c.varianceCents !== 0));

  const countedText = (c: SessionCount) => (c.uncounted ? t('tiroir non compté') : t('compté {montant}', { montant: money(c.totalCountedCents) }));
  const steps: { label: string; time: string; detail: string; color: string }[] = [
    { label: t('Ouverture'), time: heureDe(d.openedAtFiscal), detail: [d.openingOperator, t('fond {montant}', { montant: money(d.openingFloatCents) })].filter(Boolean).join(' · '), color: 'var(--teal-500)' },
    ...shifts.map((c) => ({ label: t('Relève d’équipe'), time: heureDe(c.datetimeFiscal), detail: [c.operator, countedText(c)].filter(Boolean).join(' · '), color: 'var(--blue)' })),
    isOpen
      ? { label: t('Clôture'), time: '—', detail: t('session en cours'), color: 'var(--ink-300)' }
      : { label: t('Clôture'), time: heureDe(d.closedAtFiscal), detail: [d.closingOperator, closing ? countedText(closing) : null].filter(Boolean).join(' · '), color: 'var(--violet)' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
        {steps.map((s, i) => (
          <div key={i} style={{ flex: 1, minWidth: 0, display: 'flex', gap: 9, alignItems: 'flex-start', padding: '10px 12px', borderRight: i < steps.length - 1 ? '1px solid var(--ink-100)' : 'none' }}>
            <span style={{ width: 9, height: 9, borderRadius: 'var(--r-pill)', background: s.color, marginTop: 3, flexShrink: 0 }} />
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ink-500)', fontWeight: 600 }}>{s.label}</span>
              <span style={{ display: 'block', fontSize: 'var(--t-h4)', fontWeight: 800, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{s.time}</span>
              <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.detail}</span>
            </span>
          </div>
        ))}
      </div>

      {annotated.length > 0 ? (
        <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
          {annotated.map((c, i) => <LigneComptage key={`${c.countType}-${i}`} c={c} />)}
        </div>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,300px),1fr))', gap: 12, alignItems: 'start' }}>
        <DrawerCard title={t('Fond de caisse — matin')} badge={correctionCents === null ? null : correctionCents === 0
          ? { text: t('Conforme'), bg: 'var(--teal-100)', ink: 'var(--teal-800)' }
          : { text: t('Corrigé à la main'), bg: 'var(--amber-bg)', ink: 'var(--amber-fg)' }}>
          <DrawerLine label={t('Proposé — le reste de la veille')} value={d.carriedOverCents === null ? '—' : money(d.carriedOverCents)} />
          <DrawerLine label={t('Déclaré à l’ouverture')} value={money(d.openingFloatCents)} />
          {/* ⚠️ LE MATIN COMPTÉ N'EST PAS LE FOND DÉCLARÉ : c'est de lui que part le théorique du soir. */}
          {opening && !opening.uncounted && opening.totalCountedCents !== d.openingFloatCents ? (
            <DrawerLine label={t('Compté à l’ouverture')} value={money(opening.totalCountedCents)} />
          ) : null}
          <DrawerNote text={correctionCents === null
            ? t('Première ouverture de cette caisse : aucun reste de la veille à proposer.')
            : correctionCents === 0
              ? t('Conforme au reste de la veille : rien n’a bougé pendant la nuit.')
              : t('Corrigé à la main : {ecart} par rapport au reste de la veille.', { ecart: ecartSigne(correctionCents) })} />
        </DrawerCard>

        <DrawerCard title={t('Dépôt banque — soir')} badge={null}>
          {cashDeposits.length > 0 ? (
            <>
              <DrawerLine label={t('Espèces déposées')} value={money(depositedCents)} />
              <DrawerLine label={t('Bordereau')} value={cashDeposits.map((x) => x.slipRef).filter(Boolean).join(' · ') || '—'} mono />
              <DrawerNote text={depositedDenominations(cashDeposits)} />
            </>
          ) : (
            <span style={{ display: 'block', padding: '5px 0', fontSize: '12.5px', color: 'var(--ink-500)' }}>
              {isOpen ? t('Session en cours : le dépôt se fait à la clôture.') : t('Aucun dépôt ce soir — tout reste au tiroir.')}
            </span>
          )}
          {/* ⚠️ Les chèques ne sortent pas du tiroir, mais leur remise est un fait de la journée : elle se lit ici. */}
          {chequeDeposits.length > 0 ? (
            <DrawerLine label={t('Chèques remis')} value={money(chequeDeposits.reduce((s, x) => s + x.amountCents, 0))} />
          ) : null}
          {d.drawerLeft ? (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 6, padding: '8px 10px', borderRadius: 'var(--r-sm)', background: 'var(--teal-50)' }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--teal-800)' }}>{t('Reste au tiroir — le fond proposé demain matin')}</span>
              <strong style={{ fontSize: 'var(--t-control)', color: 'var(--teal-800)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{money(d.drawerLeft.amountCents)}</strong>
            </div>
          ) : null}
        </DrawerCard>
      </div>

      <DrawerCard title={t('Du fond au compté')} badge={null}>
        <CalcLine
          label={d.openingDrawerCents === d.openingFloatCents ? t('Fond déclaré à l’ouverture') : t('Compté à l’ouverture')}
          sub={[heureDe(d.openedAtFiscal), d.openingOperator].filter(Boolean).join(' · ')}
          value={'+' + money(d.openingDrawerCents)} />
        <CalcLine label={t('Ventes en espèces')} sub={t('Encaissements nets des retours')}
          value={d.cashSalesCents === null ? t('à la clôture') : ecartSigne(d.cashSalesCents)} />
        {d.movements.map((m, i) => {
          // ⚠️ Une sorte de mouvement inconnue s'affiche telle quelle, sans signe : elle n'entre dans aucun total.
          const sign = m.movementType === 'deposit_in' ? 1 : LIBELLE_MOUVEMENT[m.movementType] ? -1 : 0;
          return (
            <CalcLine key={i}
              label={[LIBELLE_MOUVEMENT[m.movementType] ? t(LIBELLE_MOUVEMENT[m.movementType]!) : m.movementType, m.reason].filter(Boolean).join(' — ')}
              sub={[heureDe(m.datetimeFiscal), m.operator].filter(Boolean).join(' · ')}
              value={sign === 0 ? money(m.amountCents) : ecartSigne(sign * m.amountCents)}
              ink={sign > 0 ? 'var(--teal-800)' : sign < 0 ? 'var(--rose-fg)' : 'var(--ink-900)'} />
          );
        })}
        <DrawerLine label={t('Théorique')} strong value={closing ? money(closing.expectedCents) : t('à la clôture')} />
        <DrawerLine label={t('Compté à la clôture')} value={!closing ? t('à la clôture') : closing.uncounted ? t('non compté') : money(closing.totalCountedCents)} />
        {closing ? (
          closing.uncounted ? (
            <DrawerNote text={t('Tiroir non compté à la clôture : aucun écart ne se mesure.')} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, padding: '8px 10px', borderRadius: 'var(--r-sm)', background: closing.varianceCents ? 'var(--amber-bg)' : 'var(--teal-100)' }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--t-dense)', color: closing.varianceCents ? 'var(--amber-fg)' : 'var(--teal-800)', lineHeight: 1.4 }}>
                <strong>{t('Écart')}</strong>{' — '}{closing.varianceCents
                  ? t('À expliquer dans « Écarts de caisse » : la justification s’ajoute à côté, le compté ne change pas.')
                  : t('Le tiroir tombe juste.')}
              </span>
              <strong style={{ fontSize: 'var(--t-control)', color: closing.varianceCents ? 'var(--amber-fg)' : 'var(--teal-800)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{ecartSigne(closing.varianceCents)}</strong>
            </div>
          )
        ) : null}
      </DrawerCard>

      <DenominationTable morning={opening} evening={closing} deposits={cashDeposits} />
      <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', lineHeight: 1.45 }}>
        {t('Le comptage du tiroir est de la gestion : il n’est pas signé NF525 et ne change rien au Z. Les coupures viennent de la caisse — l’opérateur les a comptées.')}
      </span>
    </div>
  );
}

/** Un montant de la devise du magasin. */
function money(cents: number): string {
  return formatMoney(cents, { thousandsSep: ' ' });
}

/**
 * Le nom d'une coupure : « 50 € », « 0,50 € ».
 * ⚠️ Les coupures sont celles de l'euro (`core-cash`) : une coupure entière se nomme sans décimales, comme on la dit.
 */
function denominationName(valueCents: number): string {
  return valueCents % 100 === 0 ? formatMoneyCompact(valueCents) : money(valueCents);
}

/** « Sortis : 4 × 50 € · 2 × 20 € » — ce que les dépôts ont sorti, ou ce qu'on n'en sait pas. */
function depositedDenominations(deposits: SessionDeposit[]): string {
  if (deposits.some((x) => x.denominations === null)) return t('Coupures sorties non détaillées par la caisse.');
  const total = new Map<number, number>();
  for (const x of deposits) for (const c of x.denominations ?? []) total.set(c.valueCents, (total.get(c.valueCents) ?? 0) + c.count);
  const parts = [...total].filter(([, n]) => n > 0).sort((a, b) => b[0] - a[0]).map(([v, n]) => `${n} × ${denominationName(v)}`);
  return t('Sortis : {coupures}', { coupures: parts.join(' · ') || '—' });
}

/** Une carte du tiroir : son titre en capitales, sa pastille d'état éventuelle, ses lignes. */
function DrawerCard({ title, badge, children }: {
  title: string; badge: { text: string; bg: string; ink: string } | null; children: React.ReactNode;
}) {
  return (
    <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', padding: '13px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ink-500)', fontWeight: 600 }}>{title}</span>
        {badge ? (
          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 8px', borderRadius: 'var(--r-pill)', background: badge.bg, color: badge.ink, fontSize: '10.5px', fontWeight: 700, whiteSpace: 'nowrap' }}>{badge.text}</span>
        ) : null}
      </div>
      {children}
    </div>
  );
}

/** Une ligne « libellé → montant » d'une carte du tiroir. */
function DrawerLine({ label, value, strong, mono }: { label: string; value: string; strong?: boolean; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '5px 0', fontSize: '12.5px' }}>
      <span style={{ flex: 1, minWidth: 0, color: 'var(--ink-600)' }}>{strong ? <strong style={{ color: 'var(--ink-800)' }}>{label}</strong> : label}</span>
      <strong style={{ color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', ...(mono ? { fontFamily: 'var(--font-mono)', fontSize: '11.5px' } : null) }}>{value}</strong>
    </div>
  );
}

/** Une ligne du calcul « du fond au compté » : ce qui entre ou sort, quand et par qui. */
function CalcLine({ label, sub, value, ink = 'var(--ink-900)' }: { label: string; sub: string; value: string; ink?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '5px 0', borderBottom: '1px dashed var(--ink-100)' }}>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-800)' }}>{label}</span>
        <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{sub}</span>
      </span>
      <strong style={{ fontSize: 'var(--t-lead)', color: ink, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{value}</strong>
    </div>
  );
}

/** Une note grise sous les lignes d'une carte. */
function DrawerNote({ text }: { text: string }) {
  return <span style={{ display: 'block', marginTop: 4, fontSize: 'var(--t-note)', color: 'var(--ink-500)', lineHeight: 1.45 }}>{text}</span>;
}

/** Les colonnes de la table des coupures : coupure, matin (nombre, montant), soir (nombre, montant), dépôt. */
const DENOMINATION_COLUMNS = 'minmax(64px,1fr) 52px 86px 52px 86px 56px';

/**
 * COUPURE PAR COUPURE — le matin, le soir, et ce que le dépôt a sorti.
 * ⚠️ Les billets de 100, 200 et 500 € ne s'affichent que s'ils apparaissent dans l'un des comptages (le dessin s'arrête à
 *    50 €) : les montrer vides alourdirait la table, les cacher quand ils sont là ferait perdre un billet.
 */
function DenominationTable({ morning, evening, deposits }: { morning: SessionCount | null; evening: SessionCount | null; deposits: SessionDeposit[] }) {
  const deposited = new Map<number, number>();
  const depositKnown = deposits.every((x) => x.denominations !== null);
  for (const x of deposits) for (const c of x.denominations ?? []) deposited.set(c.valueCents, (deposited.get(c.valueCents) ?? 0) + c.count);
  const countOf = (c: SessionCount | null, v: number) => c?.denominations?.find((x) => x.valueCents === v)?.count ?? 0;
  const isPresent = (v: number) => countOf(morning, v) > 0 || countOf(evening, v) > 0 || (deposited.get(v) ?? 0) > 0;
  const bills = [50000, 20000, 10000, 5000, 2000, 1000, 500].filter((v) => v <= 5000 || isPresent(v));
  const coins = [200, 100, 50, 20, 10, 5, 2, 1];
  /** Ce qu'une colonne dit quand elle n'a pas de coupures : le tiroir n'a pas été compté, ou le détail se lit mal. */
  const missing = (c: SessionCount | null) => (!c ? '—' : c.uncounted ? t('non compté') : c.unreadable ? t('illisible') : null);
  const totalOf = (c: SessionCount | null) => missing(c) ?? money(c!.totalCountedCents);
  const cell = (c: SessionCount | null, v: number) => {
    if (missing(c) !== null) return { n: '', amount: '' };
    const n = countOf(c, v);
    return { n: n ? String(n) : '—', amount: n ? money(n * v) : '' };
  };
  const row = (v: number) => {
    const m = cell(morning, v);
    const e = cell(evening, v);
    const x = deposited.get(v) ?? 0;
    return (
      <div key={v} style={{ display: 'grid', gridTemplateColumns: DENOMINATION_COLUMNS, gap: 8, alignItems: 'center', padding: '6px 12px', borderTop: '1px solid var(--ink-100)', fontSize: '12.5px', fontVariantNumeric: 'tabular-nums' }}>
        <span style={{ fontWeight: 600, color: 'var(--ink-800)' }}>{denominationName(v)}</span>
        <span style={{ textAlign: 'right', color: m.amount ? 'var(--ink-900)' : 'var(--ink-300)' }}>{m.n}</span>
        <span style={{ textAlign: 'right', color: 'var(--ink-500)' }}>{m.amount}</span>
        <span style={{ textAlign: 'right', color: e.amount ? 'var(--ink-900)' : 'var(--ink-300)' }}>{e.n}</span>
        <span style={{ textAlign: 'right', color: 'var(--ink-500)' }}>{e.amount}</span>
        <span style={{ textAlign: 'right', fontWeight: 700, color: x ? 'var(--teal-800)' : 'var(--ink-300)' }}>{depositKnown ? (x ? String(x) : '—') : ''}</span>
      </div>
    );
  };
  const headStyle = { fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '.08em', textTransform: 'uppercase' as const, color: 'var(--ink-500)', fontWeight: 600 };
  const subheading = (text: string) => (
    <div style={{ padding: '6px 12px', background: 'var(--ink-50)', borderTop: '1px solid var(--ink-100)', ...headStyle }}>{text}</div>
  );
  return (
    <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: DENOMINATION_COLUMNS, gap: 8, padding: '8px 12px', ...headStyle }}>
        <span>{t('Coupure')}</span><span style={{ textAlign: 'right' }}>{t('Matin')}</span><span /><span style={{ textAlign: 'right' }}>{t('Soir')}</span><span /><span style={{ textAlign: 'right' }}>{t('Dépôt')}</span>
      </div>
      {subheading(t('Billets'))}
      {bills.map(row)}
      {subheading(t('Pièces'))}
      {coins.map(row)}
      <div style={{ display: 'grid', gridTemplateColumns: DENOMINATION_COLUMNS, gap: 8, alignItems: 'center', padding: '9px 12px', borderTop: '1px solid var(--ink-200)', background: 'var(--ink-50)', fontSize: '12.5px', fontWeight: 800, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>
        <span>{t('Total')}</span><span /><span style={{ textAlign: 'right' }}>{totalOf(morning)}</span><span /><span style={{ textAlign: 'right' }}>{totalOf(evening)}</span>
        <span style={{ textAlign: 'right', color: 'var(--teal-800)' }}>{deposits.length ? money(deposits.reduce((s, x) => s + x.amountCents, 0)) : '—'}</span>
      </div>
    </div>
  );
}

/**
 * LA JOURNÉE FISCALE COURANTE — pas le jour du calendrier.
 *
 * ⚠️ SANS LA BASCULE, UN COMMERCE EN NOCTURNE SE CROIT DEMAIN À 00h30. Avec une bascule à 02:00, une
 * soirée qui se prolonge appartient toujours à la journée qui l'a commencée (§9.4bis) — c'est la règle
 * que la caisse applique pour dater ses pièces et ses sessions, et l'écran doit lire le même calendrier
 * qu'elle. Miroir exact de `fiscalDayOf` du cœur : bascule 0 = jour calendaire, heure avant la bascule
 * = journée de la veille.
 *
 * ⚠️ LE DÉCALAGE SE FAIT SUR L'INSTANT, PAS SUR LA DATE : reculer l'horloge de la bascule puis prendre
 * la date traverse correctement les fins de mois et les années bissextiles, là où soustraire un jour à
 * « 01 » demanderait de connaître la longueur du mois précédent.
 */
export function jourFiscalCourant(now: Date, cutoffMinutes: number): string {
  const decale = new Date(now.getTime() - cutoffMinutes * 60_000);
  return jourYmd(decale);
}

/** Ce qu'une carte de caisse annonce : son libellé, son dessin et sa teinte. */
interface EtatCaisse {
  displayLabel: string;
  icone: IconName;
  teinte: string;
  fond: string;
}

/**
 * L'ÉTAT D'UNE CAISSE SUR UNE JOURNÉE — quatre situations, et pas deux.
 *
 * ⚠️ « CAISSE ENCORE OUVERTE » PROMETTAIT UN AVENIR QUI N'ARRIVERA PAS (remarque utilisateur
 * 2026-08-21). Sur la journée en cours, c'est vrai : le commerçant peut encore clôturer. Sur une
 * journée révolue, c'est faux — la session ne sera jamais clôturée normalement ; au mieux la caisse
 * la fermera administrativement à son prochain démarrage, SANS comptage (§9.2bis). Les deux méritent
 * donc deux phrases : « Caisse ouverte » décrit un fait normal, « Caisse non clôturée » désigne un
 * travail à faire.
 *
 * ⚠️ QUATRE ÉTATS PLUTÔT QU'UN LIBELLÉ UNIQUE. « Caisse non clôturée » partout aurait couvert les deux
 * cas — et fait crier à l'anomalie sur une caisse qui tourne normalement à 15h. Ce sont deux
 * situations opposées : l'une n'appelle aucune action, l'autre en appelle une.
 *
 * ⚠️ « NON OUVERTE » N'EST PAS « FERMÉE » : c'est une caisse qui a encaissé sans qu'aucune session
 * n'existe en base — l'historique repris, typiquement. Dire « fermée » affirmerait une clôture.
 */
function etatDeCaisse(r: CashRegisterDay, revolue: boolean): EtatCaisse {
  // ⚠️ L'ÉTAT SEUL EN TÊTE, LES HORAIRES DANS LE CORPS : l'en-tête est en `nowrap` avec ellipse, et y
  //    empiler l'état et deux heures aurait tronqué l'un ou les autres sur une carte étroite.
  if (r.closedAtFiscal !== null) {
    // ⚠️ UNE CLÔTURE DATÉE D'UN AUTRE JOUR EST UNE CLÔTURE AUTOMATIQUE (§9.2bis) : la caisse a fermé
    //    la session de la veille à son démarrage, SANS comptage du tiroir. Le taire ferait lire un
    //    « écart 0,00 € » comme un tiroir vérifié, alors que personne n'a compté.
    return r.autoClosed
      ? { displayLabel: t('Clôturée automatiquement'), icone: 'alert', teinte: 'var(--amber-fg)', fond: 'var(--amber-bg)' }
      : { displayLabel: t('Caisse clôturée'), icone: 'check', teinte: 'var(--teal-800)', fond: 'var(--teal-100)' };
  }
  if (r.sessionId === null) {
    // ⚠️ NE JAMAIS DIRE « NON OUVERTE » D'UNE CAISSE QUI A ENCAISSÉ (défaut signalé 2026-08-21) : la
    //    carte affiche son chiffre d'affaires et ses pièces juste en dessous, et se contredisait. Une
    //    caisse qui a vendu ÉTAIT ouverte ; ce qui manque, c'est la LIGNE de session — pas l'ouverture.
    return r.pieces > 0
      ? { displayLabel: t('Session non rattachée'), icone: 'alert', teinte: 'var(--amber-fg)', fond: 'var(--amber-bg)' }
      : { displayLabel: t('Caisse non ouverte'), icone: 'drawer', teinte: 'var(--ink-600)', fond: 'var(--ink-100)' };
  }
  return revolue
    ? { displayLabel: t('Caisse non clôturée'), icone: 'alert', teinte: 'var(--amber-fg)', fond: 'var(--amber-bg)' }
    : { displayLabel: t('Caisse ouverte'), icone: 'clock', teinte: 'var(--ink-700)', fond: 'var(--ink-100)' };
}

/**
 * LES VIGNETTES À DESSINER — celles de la journée, complétées par les caisses connues restées fermées.
 *
 * ⚠️ RIEN N'EST INVENTÉ, ET RIEN N'EST DE TROP : une vignette ajoutée correspond à une caisse qui
 * EXISTAIT CE JOUR-LÀ (`expectedRegisters`, borné par sa période de vie côté serveur), et tous ses
 * chiffres sont à zéro parce qu'il ne s'est rien passé. Son état se lira « Caisse non ouverte ».
 * ⚠️ On dessinait auparavant TOUTES les caisses jamais connues : une caisse de reprise apparaissait sur
 * les ventes d'aujourd'hui, et une caisse en service sur des mois où elle n'était pas installée.
 *
 * ⚠️ `expectedCashCents` ET `varianceCents` RESTENT `null`, jamais zéro : aucun tiroir n'a été compté ce
 * jour-là. Un « 0,00 € » se lirait « compté, et ça tombe juste ».
 */
function completerCaisses(vue: CashDayView | null): CashRegisterDay[] {
  if (!vue) return [];
  const vues = new Set(vue.registers.map((r) => r.registerId));
  const absentes: CashRegisterDay[] = vue.expectedRegisters
    .filter((id) => !vues.has(id))
    .map((registerId) => ({
      registerId,
      sessionId: null,
      status: null,
      openedAtFiscal: null,
      closedAtFiscal: null,
      netCents: 0,
      titlesIssuedCents: 0,
      pieces: 0,
      expectedCashCents: null,
      varianceCents: null,
      autoClosed: false,
      varianceCountId: null,
      justification: null,
      reprise: estCaisseReprise(registerId),
    }));
  return [...vue.registers, ...absentes].sort((a, b) => a.registerId.localeCompare(b.registerId));
}
